import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, ShieldCheck, TrendingDown, Truck, Users, Award, ArrowRight, Star, Quote } from "lucide-react";

const PILLARS = [
  {
    Icon: Package, title: "Exhaustive Inventory",
    tagline: "2000+ SKUs, Always in Stock",
    desc: "We maintain one of the widest product portfolios in Hyderabad — 2000+ SKUs across cables, networking, peripherals, CCTV, and enterprise solutions. No backorders, no delays.",
    highlight: "2000+ Products"
  },
  {
    Icon: ShieldCheck, title: "Strict Quality Governance",
    tagline: "Zero Out-of-Box Failure Rate",
    desc: "Every product undergoes stringent incoming quality inspection. We partner only with reputable global brands and maintain zero tolerance for defective merchandise reaching clients.",
    highlight: "Zero Defects Policy"
  },
  {
    Icon: TrendingDown, title: "Competitive Pricing",
    tagline: "Aggressive B2B Pricing",
    desc: "Our scaled pricing model ensures you get the best market rates — especially for bulk and repeat orders. Custom quotes available for project-based procurement within 24 hours.",
    highlight: "Best Market Rates"
  },
  {
    Icon: Truck, title: "Agile Logistics Fleet",
    tagline: "Fast, Reliable Dispatch",
    desc: "With a localized supply chain and dedicated logistics fleet, we ensure same-day or next-day dispatch for most orders in Hyderabad and Secunderabad.",
    highlight: "Same-Day Dispatch"
  },
  {
    Icon: Users, title: "End-to-End Partner Satisfaction",
    tagline: "More Than Just a Vendor",
    desc: "We act as your IT supply chain partner — offering pre-sales consultation, product specification support, and post-delivery assistance for every order.",
    highlight: "Full-Cycle Support"
  },
  {
    Icon: Award, title: "Brand Authorized Distributor",
    tagline: "11 Trusted Brands",
    desc: "As authorized distributors for Dell, HP, ATEN, Hikvision, Honeywell, D-Link, TP-Link, and others, we guarantee genuine, warranty-backed products with full documentation.",
    highlight: "Warranty Guaranteed"
  },
];

const STATS = [
  { value: "07+", label: "Years in Business" },
  { value: "1000+", label: "Clients Served" },
  { value: "2000+", label: "Products Available" },
  { value: "11", label: "Brand Partners" },
];

const TESTIMONIALS = [
  {
    name: "Rajesh Kumar",
    role: "IT Manager",
    org: "Hyderabad Corporate Office",
    rating: 5,
    text: "Jain Infotech has been our preferred IT hardware partner for 3 years. Their quick turnaround, quality products, and competitive pricing have helped us deliver our IT projects on time and within budget."
  },
  {
    name: "Priya Sharma",
    role: "Technical Director",
    org: "SecureSystems SI, Secunderabad",
    rating: 5,
    text: "As a system integrator, we need a reliable supply chain for CCTV and networking projects. Jain Infotech never disappoints — their Hikvision cameras and Cat6 cabling are always genuine and on-time."
  },
  {
    name: "Mohammed Ali",
    role: "Procurement Head",
    org: "State Government Department",
    rating: 5,
    text: "We've placed bulk orders for Cat6 cables, CCTV systems, and managed switches through Jain Infotech. Their pricing is highly competitive and documentation process is clean for government procurement."
  },
  {
    name: "Srinivas Rao",
    role: "Infrastructure Lead",
    org: "Hyderabad IT Park",
    rating: 5,
    text: "ATEN KVM solutions recommended by Jain Infotech have transformed our server room operations. They provided excellent technical guidance and ensured we got the right models for our setup."
  },
];

function StarRating({ count }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: count }, (_, i) => (
        <Star key={`star-${i}`} size={13} fill="#F59E0B" stroke="none" />
      ))}
    </div>
  );
}

export default function WhyChooseUs() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-[#2D3748] py-14 px-6" data-testid="why-hero">
        <div className="max-w-7xl mx-auto">
          <nav className="text-xs text-gray-500 mb-4">
            <Link to="/" className="hover:text-gray-300 transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-300">Why Choose Us</span>
          </nav>
          <h1 className="font-heading text-4xl lg:text-5xl font-black text-white mb-3">Why Choose Jain Infotech?</h1>
          <p className="text-gray-400 text-base max-w-2xl leading-relaxed">
            We're not just a distributor. We're your strategic IT supply chain partner — built on quality, reliability, and competitive pricing.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#0F62FE] py-10" data-testid="why-stats-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }}
                className="text-center" data-testid={`why-stat-${i}`}>
                <p className="text-3xl lg:text-4xl font-black text-white font-heading">{s.value}</p>
                <p className="text-blue-200 text-sm mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Pillars */}
      <section className="py-16 bg-white" data-testid="value-pillars-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl lg:text-4xl font-black text-[#111827] mb-3">Our Competitive Advantages</h2>
            <p className="text-[#4B5563] text-base max-w-xl mx-auto">Six reasons why 1000+ clients choose Jain Infotech for their IT procurement needs</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {PILLARS.map(({ Icon, title, tagline, desc, highlight }, i) => (
              <motion.div key={title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}
                className="p-6 border border-gray-200 hover:border-[#0F62FE] hover:shadow-md transition-all duration-200 group bg-white"
                data-testid={`pillar-card-${i}`}>
                <div className="w-12 h-12 bg-[#EFF6FF] flex items-center justify-center mb-4 group-hover:bg-[#0F62FE] transition-colors">
                  <Icon size={22} className="text-[#0F62FE] group-hover:text-white transition-colors" />
                </div>
                <span className="inline-block text-xs font-bold text-[#0F62FE] bg-blue-50 border border-blue-200 px-2 py-0.5 mb-2">{highlight}</span>
                <h3 className="font-heading font-black text-[#111827] text-base mb-1">{title}</h3>
                <p className="text-xs font-semibold text-[#4B5563] mb-3">{tagline}</p>
                <p className="text-sm text-[#4B5563] leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-[#F3F4F6]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl lg:text-4xl font-black text-[#111827] mb-3">What Our Clients Say</h2>
            <p className="text-[#4B5563] text-base">Trusted by enterprises, system integrators & government bodies</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {TESTIMONIALS.map(({ name, role, org, rating, text }, i) => (
              <motion.div key={name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}
                className="p-6 bg-white border border-gray-200 hover:border-[#0F62FE] hover:shadow-md transition-all duration-200"
                data-testid={`testimonial-${i}`}>
                <Quote size={24} className="text-[#0F62FE] opacity-30 mb-3" />
                <p className="text-[#374151] text-sm leading-relaxed mb-4 italic">"{text}"</p>
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div>
                    <p className="font-bold text-[#111827] text-sm">{name}</p>
                    <p className="text-xs text-[#4B5563]">{role}</p>
                    <p className="text-xs text-[#0F62FE]">{org}</p>
                  </div>
                  <StarRating count={rating} />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-[#2D3748]" data-testid="why-cta-section">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl lg:text-3xl font-black text-white mb-3">Ready to Experience the Difference?</h2>
          <p className="text-gray-400 text-base mb-7 max-w-md mx-auto">
            Join 1000+ satisfied clients who rely on Jain Infotech for their IT infrastructure needs.
          </p>
          <Link to="/contact" data-testid="why-cta-btn" className="inline-flex items-center gap-2 bg-[#0F62FE] hover:bg-[#0052CC] text-white font-semibold px-7 py-3.5 transition-all duration-200 hover:shadow-xl">
            Get a Quote <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
